import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  Controller,
  DeepMap,
  FieldError,
  useForm,
  UseFormRegister,
} from 'react-hook-form';
import toast from 'react-hot-toast';
import * as yup from 'yup';

import { Modal } from '@/components/common/modal';
import { Button, ButtonVariant } from '@/components/form/button';
import { Input } from '@/components/form/input';
import { Select } from '@/components/form/select';
import { SignedInMethod, useAuth } from '@/features/auth';
import { Method } from '@/features/single-measurement/types';
import { Tags } from '@/features/single-measurement/types/tags';
import { getSugarUnitOptions, useSettings } from '@/features/user-settings';
import { SugarUnit } from '@/features/user-settings';
import { IDropdownProps } from '@/features/user-settings/components/settings-dropdown-item';
import { getDateString } from '@/utils/date-time';

import { createFermentationn } from '../../services/fermentations';
import { IFermentation } from '../../types/fermentation';
import { Grapes } from '../../types/grape-types';
import { YeastBrands } from '../../types/yeast-brands';
import { shouldRenderInput } from '../../utils/conditional-input-render';
import {
  getGrapesOptions,
  getYeastBrandsOptions,
} from '../../utils/dropdown-option-mapper';

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** toggle modal open and close */
  toggle: () => void;
}

export type FermentationFields = {
  name: string; // required
  fermentedAt: string; // required
  scaleType: SugarUnit;
} & Tags;

const validationSchema = yup.object({
  name: yup.string().required('commonValidation.fieldRequired'),
});

const fermentationMethod = {
  [SignedInMethod.Wine]: Method.WineConzViab,
  [SignedInMethod.Beer]: Method.Yeast2ConzViab,
  [SignedInMethod.Ethanol]: Method.BioEthConzViab,
};

export const CreateFermentationModal: React.FC<IProps> = ({ isOpen, toggle }) => {
  const { settings } = useSettings();
  const { user } = useAuth();
  const signedInMethod = useMemo(() => user?.signedInMethod!, [user?.signedInMethod]);

  const yeastBrandsOptions = useMemo(() => getYeastBrandsOptions(), []);
  const sugarUnitOptions = useMemo(() => getSugarUnitOptions(), []);
  const grapesOptions = useMemo(() => getGrapesOptions(), []);

  const createFermentationMutation = useMutation({
    mutationFn: ({ fermentation }: { fermentation: Partial<IFermentation> }) =>
      createFermentationn({ ...fermentation }),
    onSuccess: () => {
      handleOnBack();
      toast.success('Fermentation has created!');
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  // based on the analysis method we switch the validation schema
  // only few input fields are getting removed from the UI based on the analysis method
  // if the validation schema become complex, consider creating two seperate validations for each type
  const {
    reset,
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FermentationFields>({
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const handleOnSubmit = handleSubmit(({ name, scaleType, fermentedAt, ...tags }) => {
    const method = fermentationMethod[signedInMethod];
    const fermentation: Partial<IFermentation> = {
      tags,
      name,
      method,
      scaleType,
      fermentedAt: fermentedAt,
    };
    createFermentationMutation.mutate({ fermentation });
  });

  // is user click 'Back', this method will be called
  const handleOnBack = () => {
    reset();
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleOnBack}
      title="New Tracking"
      subtitle={new Date().toLocaleString()}
      conatinerClassnames="w-full 2xl:w-2/3"
    >
      <form>
        <div className="flex gap-20 p-20">
          <div className="flex flex-col flex-1 gap-6">
            {shouldRenderInput({ input: 'name', method: signedInMethod }) && (
              <TextInput name="name" label="Name" register={register} errors={errors} />
            )}

            {shouldRenderInput({ input: 'fermentedAt', method: signedInMethod }) && (
              <div className="flex items-center justify-between">
                <label htmlFor="date">Started At</label>
                <Input<FermentationFields>
                  className="w-60"
                  name="fermentedAt"
                  register={register}
                  type="datetime-local"
                  defaultValue={getDateString()}
                />
              </div>
            )}

            {shouldRenderInput({ input: 'scaleType', method: signedInMethod }) && (
              <SelectInput
                name="scaleType"
                control={control}
                label="Sugar Unit"
                defaultValue={settings.sugarUnit}
                options={sugarUnitOptions}
                disabled
              />
            )}

            {shouldRenderInput({ input: 'oculyzeWineType', method: signedInMethod }) && (
              <SelectInput
                control={control}
                label="Grape Type"
                name="oculyzeWineType"
                defaultValue={Grapes.White}
                options={grapesOptions}
              />
            )}

            {shouldRenderInput({ input: 'grapeVariety', method: signedInMethod }) && (
              <TextInput name="grapeVariety" label="Grape Variety" register={register} />
            )}

            {shouldRenderInput({ input: 'vineyardSource', method: signedInMethod }) && (
              <TextInput
                name="vineyardSource"
                label="Vineyard/Source"
                register={register}
              />
            )}
          </div>

          <div className="flex flex-col flex-1 gap-6">
            {shouldRenderInput({ input: 'tank', method: signedInMethod }) && (
              <TextInput name="tank" label="Tank" register={register} />
            )}

            {shouldRenderInput({ input: 'tankVolume', method: signedInMethod }) && (
              <TextInput name="tankVolume" label="Tank Volume" register={register} />
            )}

            {shouldRenderInput({ input: 'yeastBrand', method: signedInMethod }) && (
              <SelectInput
                name="yeastBrand"
                control={control}
                label="Yeast Brand"
                defaultValue={YeastBrands.Enartis}
                options={yeastBrandsOptions}
              />
            )}

            {shouldRenderInput({ input: 'yeastStrain', method: signedInMethod }) && (
              <TextInput name="yeastStrain" label="Yeast Strain" register={register} />
            )}

            {shouldRenderInput({ input: 'yeastGeneration', method: signedInMethod }) && (
              <TextInput
                name="yeastGeneration"
                label="Yeast Generation"
                register={register}
              />
            )}

            {shouldRenderInput({
              input: 'nutrientsAtFillIn',
              method: signedInMethod,
            }) && (
              <TextInput
                register={register}
                name="nutrientsAtFillIn"
                label="Nutrients at Inoculation"
              />
            )}

            {shouldRenderInput({
              input: 'additivesAtFillIn',
              method: signedInMethod,
            }) && (
              <TextInput
                register={register}
                name="additivesAtFillIn"
                label="Additives at inoculation"
              />
            )}
          </div>
        </div>
      </form>
      <div className="flex justify-end gap-6">
        <Button variant={ButtonVariant.Secondary} onClick={handleOnBack} type="button">
          Back
        </Button>
        <Button onClick={handleOnSubmit} type="button">
          Create
        </Button>
      </div>
    </Modal>
  );
};

const TextInput = ({
  label,
  name,
  ...props
}: {
  label: string;
  name: keyof FermentationFields;
  errors?: Partial<DeepMap<FermentationFields, FieldError>>;
  register?: UseFormRegister<FermentationFields>;
}) => {
  return (
    <div className="flex items-center justify-between">
      <label htmlFor="name">{label}</label>
      <Input<FermentationFields>
        name={name}
        placeholder={label}
        className="w-60"
        {...props}
      />
    </div>
  );
};

const SelectInput: React.FC<IDropdownProps & { disabled?: boolean }> = ({
  name,
  label,
  options,
  control,
  defaultValue,
  parser = String,
  disabled = false,
}) => {
  return (
    <div className="flex items-center justify-between">
      <label htmlFor="scaleType">{label}</label>
      <Controller
        render={({ field: { onChange, value } }) => (
          <Select
            defaultValue={value}
            onChange={(value) => onChange(parser(value))}
            options={[...options.map(({ label, value }) => ({ label, value }))]}
            classnames="w-60"
            menuItemClassnames="w-60"
            disabled={disabled}
          />
        )}
        control={control}
        name={name}
        defaultValue={defaultValue}
      />
    </div>
  );
};
